import { Button, Checkbox, Dialog, Flex, FormStatus, Input, P } from '@dnb/eufemia';
import { email_medium as EmailMediumIcon } from '@dnb/eufemia/icons';
import type { ApiDto } from '@portals/shared/portal/ApiDto';
import { useEufemiaForm } from '@portals/shared-frontend/hooks/useEufemiaForm';
import { type JSX, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { z } from 'zod';

import { createSubscriptions } from '@/api/subscriptions';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';
import ModalPage from '@/components/ModalPage';
import { createDummyApi } from '@/dummyData';
import { useUser } from '@/hooks/useUser';

import style from './StatusSubscriptions.module.css';

const statusSubscriptionsFormSchema = z.object({
  email: z.string().email(),
  status: z
    .record(z.string(), z.boolean())
    .refine((status) => Object.values(status).some(Boolean), 'Select at least one API'),
});

const DUMMY_APIS_WITH_STATUS: Array<ApiDto> = [
  createDummyApi(12345678),
  createDummyApi(15555934),
  createDummyApi(15983403),
  createDummyApi(18989890),
  createDummyApi(23498744),
];

export default function StatusSubscriptions(): JSX.Element {
  const { user } = useUser();
  const {
    submitting,
    register,
    handleSubmit,
    controller: { setValue, getValue, formError, getError },
  } = useEufemiaForm(statusSubscriptionsFormSchema, {
    status: {},
  });

  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: apisData, isValidating: apisLoading } = useSWR<ApiDto[]>('/api');
  const apis = apisData || DUMMY_APIS_WITH_STATUS;
  const hasLoaded = !apisLoading && !!apis;
  const apisWithStatus = apis?.filter((api) => api.enableStatus);

  const toggleAll = () => {
    const shouldToggleOn = apisWithStatus?.map(({ id }) => getValue(`status.${id}`)).some((on) => !on);

    if (apisWithStatus) {
      for (const { id } of apisWithStatus) {
        setValue(`status.${id}`, shouldToggleOn);
      }
    }
  };

  const onSubmit = handleSubmit(
    useCallback(
      async ({ email, status }) => {
        const apiIds = apis?.map(({ id }) => id);
        await createSubscriptions({
          email,
          status: apiIds?.filter((id) => status[id]),
        });
        setOpen(true);
      },
      [apis],
    ),
  );

  useEffect(() => {
    if (user?.email) {
      setValue('email', user.email);
    }
  }, [setValue, user?.email]);

  const formEmailValue = getValue('email') as string | undefined;

  return (
    <ModalPage onClose={() => navigate('/status')} skeleton={!hasLoaded} subtitle="Subscriptions" title="Status">
      {submitting && <LoadingModal />}
      <form onSubmit={onSubmit}>
        <Container size="small">
          <Flex.Horizontal align="baseline">
            <P>Please fill in your email below to get status updates.</P>
            <Input
              icon={EmailMediumIcon}
              placeholder="Email address"
              size="large"
              stretch
              top="medium"
              type="confirmation"
              {...register.input('email')}
            />
          </Flex.Horizontal>
        </Container>
        <Flex.Horizontal align="baseline" top="large">
          <P>Please select which APIs you would like to receive status updates for.</P>
          <Button className={style['ToggleAllButton']} onClick={toggleAll} size="large" variant="tertiary">
            Toggle all
          </Button>
          {apisWithStatus?.map(({ id, name }) => (
            <Checkbox key={id} label={name} top="small" {...register.checkbox(`status.${id}`)} />
          ))}
          <FormStatus id="checkbox-status" text={getError('status')} title="Status" top="small" />
        </Flex.Horizontal>
        <Flex.Horizontal align="baseline" top="medium">
          <Button disabled={submitting} size="large" status={formError} type="submit">
            Confirm
          </Button>
        </Flex.Horizontal>
      </form>
      <Dialog
        confirmText="Got it!"
        description={
          <P>
            We&apos;ve sent a confirmation email to {formEmailValue}. If you didn&apos;t receive it, check your spam
            filter.
          </P>
        }
        hideDecline
        onConfirm={() => navigate('/status')}
        open={open}
        title="Success! An email is on its way"
        variant="confirmation"
      />
    </ModalPage>
  );
}
